import React, { FC } from 'react';
import { useNavigationContext, NavigationType } from 'app/components/header/navigation/Navigation';
import { HeaderSection } from 'app/components/header/navigation/HeaderNavigation';
import { DropdownSection } from 'app/components/header/navigation/DropdownNavigation';
import { DrawerSection } from 'app/components/header/navigation/DrawerNavigation';
import DiscreetErrorBoundary from 'app/components/utilities/DiscreetErrorBoundary';
import { absurd } from 'fp-ts/lib/function';

export interface INavigationSectionProps {
  title?: string;
}

const getSection = (navType: NavigationType, props: INavigationSectionProps) => {
  switch (navType) {
    case NavigationType.Drawer: {
      return <DrawerSection {...props} />;
    }
    case NavigationType.Dropdown: {
      return <DropdownSection {...props} />;
    }
    case NavigationType.Header: {
      return <HeaderSection {...props} />;
    }
    default:
      absurd(navType);
  }
};
/**
 * A navigation section that renders as a list in Dropdown or Drawer or as a Dropdown in Header.
 * It is recursive - meaning when you render
 * ```
 * <HeaderNavigation>
 *   <NavigationSection> // <- 1
 *     <NavigationSection> // <- 2
 *       ...
 *     </NavigationSection>
 *   </NavigationSection>
 * </HeaderNavigation>
 * ```
 * in header, the (1) will render as a dropdown, and (2) will render as a simple list.
 */
const NavigationSection: FC<INavigationSectionProps> = props => {
  const context = useNavigationContext();
  if (!context) return null;
  const section = getSection(context.type, props);

  return (
    <DiscreetErrorBoundary name="NavigationSection">
      {section}
    </DiscreetErrorBoundary>
  );
};

export default NavigationSection;
